import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const recruitJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/recruit`,
    url: `${siteUrl}/recruit`,
    name: '求人情報｜精密加工技術者・品質管理担当募集｜おかやまネジ製作所',
    description: '岡山市おかやまネジ製作所の求人情報。精密加工技術者・品質管理担当者を募集中。未経験者歓迎。月給22万円〜。各種社会保険完備。岡山市北区の精密ネジ製造の町工場。',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'トップ', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '求人情報', item: `${siteUrl}/recruit` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: '精密加工技術者',
    description: 'CNC旋盤を使用した精密ネジの加工業務。未経験者も歓迎いたします。先輩職人が丁寧に指導します。',
    hiringOrganization: {
      '@type': 'Organization',
      name: 'おかやまネジ製作所',
      sameAs: siteUrl,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '北区○○町1-2-3',
        addressLocality: '岡山市',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'JPY',
      value: {
        '@type': 'QuantitativeValue',
        minValue: 220000,
        maxValue: 350000,
        unitText: 'MONTH',
      },
    },
    employmentType: 'FULL_TIME',
    datePosted: '2025-07-14',
    validThrough: '2025-12-31',
    applicantLocationRequirements: {
      '@type': 'Country',
      name: 'JP',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: '品質管理担当者',
    description: '製品の検査・測定業務。三次元測定機を使用した精密測定や、検査成績書の作成などを担当していただきます。',
    hiringOrganization: {
      '@type': 'Organization',
      name: 'おかやまネジ製作所',
      sameAs: siteUrl,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '北区○○町1-2-3',
        addressLocality: '岡山市',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'JPY',
      value: {
        '@type': 'QuantitativeValue',
        minValue: 200000,
        maxValue: 300000,
        unitText: 'MONTH',
      },
    },
    employmentType: 'FULL_TIME',
    datePosted: '2025-07-14',
    validThrough: '2025-12-31',
    applicantLocationRequirements: {
      '@type': 'Country',
      name: 'JP',
    },
  },
];

const RecruitPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '求人情報｜精密加工技術者・品質管理担当募集｜おかやまネジ製作所';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '岡山市おかやまネジ製作所の求人情報。精密加工技術者・品質管理担当者を募集中。未経験者歓迎。月給22万円〜。各種社会保険完備。岡山市北区の精密ネジ製造の町工場。');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/recruit`);

    const scriptId = 'recruit-jsonld';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(recruitJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const positions = [
    {
      title: '精密加工技術者',
      description: 'CNC旋盤を使用した精密ネジの加工業務。未経験者も歓迎いたします。先輩職人が丁寧に指導しますので、安心してご応募ください。',
      requirements: [
        'やる気と向上心のある方',
        'ものづくりに興味のある方',
        '経験者優遇（未経験者も歓迎）',
        '普通自動車免許（通勤用）',
      ],
      benefits: [
        '月給: 220,000円〜350,000円（経験・能力による）',
        '昇給: 年1回',
        '賞与: 年2回（業績による）',
        '各種社会保険完備',
        '交通費支給（上限20,000円/月）',
        '退職金制度あり',
        '作業服貸与',
      ],
    },
    {
      title: '品質管理担当者',
      description: '製品の検査・測定業務。三次元測定機を使用した精密測定や、検査成績書の作成などを担当していただきます。',
      requirements: [
        '測定器の使用経験がある方（優遇）',
        'PC基本操作ができる方',
        '細かい作業が得意な方',
        '普通自動車免許（通勤用）',
      ],
      benefits: [
        '月給: 200,000円〜300,000円（経験・能力による）',
        '昇給: 年1回',
        '賞与: 年2回（業績による）',
        '各種社会保険完備',
        '交通費支給（上限20,000円/月）',
        '退職金制度あり',
        '作業服貸与',
      ],
    },
  ];

  const workplaceImages = [
    {
      url: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8101d2445dde03bd6ccca8123f89ac97.jpeg',
      caption: 'チームワークを大切にしています',
    },
    {
      url: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/675a6a796d0fee3739a0199733542ebb.jpeg',
      caption: '休憩室も完備',
    },
    {
      url: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/cd682bd78ae71caa8dc21bd4fd024280.jpeg',
      caption: '先輩が丁寧に指導します',
    },
    {
      url: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ca7c10e446254d302a789f9495bbba93.jpeg',
      caption: '営業部長のハナちゃんも毎日出社！',
    },
  ];

  const employeeVoices = [
    {
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/759fbcfd75710ca0a1268b729615256b.jpeg',
      name: '田中 健一',
      joinYear: '入社5年目',
      position: '精密加工技術者',
      comment: '未経験で入社しましたが、先輩方が丁寧に教えてくださり、今では難削材の加工も任されるようになりました。0.01mm単位の精度を出せたときの達成感は格別です。少人数の職場なので、一人ひとりの意見が尊重され、働きやすい環境です。',
    },
    {
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/62596841e99cb3a192891a664d7cbf6b.jpeg',
      name: '佐藤 誠',
      joinYear: '入社10年目',
      position: '品質管理担当',
      comment: '品質管理の仕事は責任が重いですが、やりがいがあります。お客様から「おかやまネジ製作所の製品は信頼できる」と言っていただけることが何よりの喜びです。創業50年の技術と信頼を守り続けることに誇りを持っています。',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="求人情報"
          breadcrumbs={[
            { label: 'トップ', path: '/' },
            { label: '求人情報' },
          ]}
          imageUrl="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/04e15372f95fce9e3f480e88e1e37a85.jpeg"
        />

        {/* 募集要項 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-6 sm:mb-8">
              募集職種
            </h2>
            <p className="text-base sm:text-lg text-gray-600 text-center mb-12 sm:mb-16 max-w-3xl mx-auto">
              一緒に働く仲間を募集しています。未経験者も歓迎いたします。
            </p>

            <div className="space-y-8 sm:space-y-12">
              {positions.map((position, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-navy rounded-2xl p-6 sm:p-8 lg:p-10 hover:shadow-2xl transition-all duration-300"
                >
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-4 sm:mb-6">
                    {position.title}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
                    {position.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                    <div>
                      <h4 className="text-lg sm:text-xl font-bold text-navy mb-4">
                        応募資格
                      </h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start text-sm sm:text-base text-gray-600">
                            <i className="ri-check-line text-navy text-xl mt-0.5 mr-2 flex-shrink-0"></i>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-lg sm:text-xl font-bold text-navy mb-4">
                        待遇・福利厚生
                      </h4>
                      <ul className="space-y-2">
                        {position.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start text-sm sm:text-base text-gray-600">
                            <i className="ri-check-line text-navy text-xl mt-0.5 mr-2 flex-shrink-0"></i>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-8 sm:mt-10">
                    <a
                      href="/contact"
                      className="inline-block w-full sm:w-auto bg-navy text-white px-8 sm:px-12 py-4 rounded-lg text-center text-base sm:text-lg font-bold hover:bg-navy-light transition-colors whitespace-nowrap"
                    >
                      この職種に応募する
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 職場環境 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16">
              働く環境
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              {workplaceImages.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.url}
                      alt={item.caption}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4 sm:p-6">
                    <p className="text-base sm:text-lg font-bold text-navy text-center">
                      {item.caption}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 先輩社員の声 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16">
              先輩社員の声
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {employeeVoices.map((employee, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={employee.image}
                      alt={employee.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <div className="mb-4">
                      <h3 className="text-xl sm:text-2xl font-bold text-navy mb-1">
                        {employee.name}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600">
                        {employee.joinYear} / {employee.position}
                      </p>
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {employee.comment}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 応募の流れ */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16">
              応募の流れ
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                { step: '01', title: 'お問い合わせ', desc: 'お電話またはメールでご連絡ください' },
                { step: '02', title: '書類選考', desc: '履歴書・職務経歴書をご提出ください' },
                { step: '03', title: '面接', desc: '工場見学を兼ねた面接を実施します' },
                { step: '04', title: '内定・入社', desc: '入社日はご相談に応じます' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-navy rounded-full mx-auto mb-4 sm:mb-6">
                    <span className="text-2xl sm:text-3xl font-black text-white">{item.step}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-navy mb-2 sm:mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 sm:mt-16 text-center">
              <a
                href="/contact"
                className="inline-block bg-navy text-white px-10 sm:px-16 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-bold hover:bg-navy-light transition-colors shadow-xl hover:shadow-2xl whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                応募・お問い合わせはこちら
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RecruitPage;